import { useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { isPanelRequired } from '../api/client';
import { useApi } from '../api/context';
import {
  RECONCILIATION_STATUSES,
  type Reconciliation,
  type ReconciliationRow,
  type ReconciliationStatus,
} from '../api/types';
import { DateRangeForm } from '../components/DateRangeForm';
import {
  Chip,
  ErrorNotice,
  Loading,
  PageHeader,
  RECONCILIATION_TONE,
  StatCard,
  Upsell,
} from '../components/ui';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/mk';
import { formatDate, formatDateTime, formatDay, skopjeToday } from '../lib/dates';
import { formatDen } from '../lib/money';
import {
  checkRange,
  presetRange,
  RECONCILIATION_MAX_DAYS,
  type DayRangeValue,
  type PeriodPreset,
} from '../lib/ranges';
import { useDebounced, useLoad } from '../lib/useLoad';
import { useLiveEvents } from '../live/live';
import { usePanelActive } from '../state/merchant';

const PRESETS: readonly PeriodPreset[] = ['today', 'yesterday', 'week', 'month'];

function rangeFromParams(params: URLSearchParams, today: string): DayRangeValue {
  const from = params.get('from');
  const to = params.get('to');
  if (from && to) {
    const r = { from, to };
    if (!checkRange(r, RECONCILIATION_MAX_DAYS, today)) return r;
  }
  return presetRange('week', today);
}

/** '21 уплата' / '21 payments': the plural category by the language's CLDR rules (mk: 1, 21, 31… are 'one'). */
export function countLabel(
  t: (k: MessageKey, p?: Record<string, string | number>) => string,
  n: number,
  locale: string,
): string {
  const one = new Intl.PluralRules(locale).select(n) === 'one';
  return t(one ? 'recon.count.one' : 'recon.count.other', { count: n });
}

/**
 * ZINQ's QR payments next to the receiving account's bank statement (web panel): each row says in
 * plain words whether the money is on the statement.
 */
export function ReconciliationPage() {
  const { t } = useI18n();
  const api = useApi();
  const panelActive = usePanelActive();
  const today = skopjeToday();
  const [params, setParams] = useSearchParams();
  const range = rangeFromParams(params, today);
  const [filter, setFilter] = useState<ReconciliationStatus | 'ALL'>('ALL');

  const { state, reload } = useLoad(
    (signal) => api.reconciliation(range.from, range.to, signal),
    [api, range.from, range.to, panelActive],
  );

  const refresh = useDebounced(() => reload({ quiet: true }), 1_000);
  useLiveEvents(['payment.received', 'resync'], () => {
    if (range.to >= today) refresh();
  });

  let body: ReactNode;
  if (state.status === 'loading') body = <Loading />;
  else if (state.status === 'error') {
    body = isPanelRequired(state.error) ? (
      <Upsell reason="reconciliation" />
    ) : (
      <ErrorNotice error={state.error} onRetry={() => reload()} />
    );
  } else {
    body = <ReconciliationView data={state.data} filter={filter} onFilter={setFilter} />;
  }

  return (
    <>
      <PageHeader title={t('recon.title')} subtitle={t('recon.subtitle')} />
      <DateRangeForm
        value={range}
        today={today}
        maxDays={RECONCILIATION_MAX_DAYS}
        presets={PRESETS}
        panelActive={null}
        onChange={(r) => setParams({ from: r.from, to: r.to }, { replace: true })}
      />
      <p className="muted small range-note">{t('recon.rangeNote')}</p>
      {body}
    </>
  );
}

/** The loaded reconciliation (separate for tests). */
export function ReconciliationView({
  data,
  filter,
  onFilter,
}: {
  data: Reconciliation;
  filter: ReconciliationStatus | 'ALL';
  onFilter: (f: ReconciliationStatus | 'ALL') => void;
}) {
  const { t, locale } = useI18n();
  const s = data.summary;
  const counts: Record<ReconciliationStatus, number> = {
    MATCHED: 0,
    PENDING: 0,
    MISSING_IN_BANK: 0,
    AMOUNT_MISMATCH: 0,
    OTHER_INCOMING: 0,
  };
  for (const r of data.rows) counts[r.status] = (counts[r.status] ?? 0) + 1;
  const rows = filter === 'ALL' ? data.rows : data.rows.filter((r) => r.status === filter);

  return (
    <>
      {data.account ? (
        <p className="muted" data-testid="recon-account">
          {t('recon.account', { bank: data.account.bankName, number: data.account.maskedNumber })}
        </p>
      ) : null}

      {!data.available ? (
        <div className="notice notice--warning" role="status" data-testid="recon-unavailable">
          <h2 className="notice-title">{t('recon.unavailable.title')}</h2>
          <p>{t(`recon.unavailable.${data.unavailableReason ?? 'BANK_ERROR'}`)}</p>
        </div>
      ) : null}

      <div className="stats stats--6">
        <StatCard
          label={t('recon.card.settled')}
          value={formatDen(s.zinqSettledMinor)}
          hint={countLabel(t, s.zinqSettledCount, locale)}
        />
        {data.available ? (
          <>
            <StatCard
              label={t('recon.card.matched')}
              value={formatDen(s.matchedMinor)}
              hint={countLabel(t, s.matchedCount, locale)}
              tone="success"
              testId="recon-matched"
            />
            <StatCard label={t('recon.card.pending')} value={s.pendingCount} tone="info" />
            <StatCard
              label={t('recon.card.missing')}
              value={s.missingInBankCount}
              tone={s.missingInBankCount > 0 ? 'danger' : undefined}
            />
            <StatCard
              label={t('recon.card.mismatch')}
              value={s.amountMismatchCount}
              tone={s.amountMismatchCount > 0 ? 'warning' : undefined}
            />
            <StatCard
              label={t('recon.card.other')}
              value={formatDen(s.otherIncomingMinor)}
              hint={countLabel(t, s.otherIncomingCount, locale)}
              tone="accent"
            />
          </>
        ) : null}
      </div>

      {data.available ? (
        <section className="card">
          <div className="card-header">
            <div className="segmented" role="group" aria-label={t('recon.filterLabel')}>
              <button
                type="button"
                className="segmented-item"
                aria-pressed={filter === 'ALL'}
                onClick={() => onFilter('ALL')}
              >
                {t('recon.filter.all')} <span className="count">{data.rows.length}</span>
              </button>
              {RECONCILIATION_STATUSES.map((st) => (
                <button
                  key={st}
                  type="button"
                  className="segmented-item"
                  aria-pressed={filter === st}
                  onClick={() => onFilter(st)}
                >
                  {t(`recon.status.${st}`)} <span className="count">{counts[st]}</span>
                </button>
              ))}
            </div>
          </div>
          {rows.length === 0 ? (
            <p className="empty">{t('recon.empty')}</p>
          ) : (
            <div className="table-wrap">
              <table className="table table--recon" data-testid="recon-table">
                <caption className="visually-hidden">
                  {t('recon.caption', { from: formatDay(data.from), to: formatDay(data.to) })}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('recon.col.status')}</th>
                    <th scope="col">{t('recon.col.zinqTime')}</th>
                    <th scope="col">{t('recon.col.bankDate')}</th>
                    <th scope="col">{t('recon.col.who')}</th>
                    <th scope="col">{t('recon.col.reference')}</th>
                    <th scope="col" className="num">
                      {t('recon.col.zinqAmount')}
                    </th>
                    <th scope="col" className="num">
                      {t('recon.col.bankAmount')}
                    </th>
                    <th scope="col" className="num">
                      {t('recon.col.net')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    // A payment can have two rows (its match + an extra credit with its id).
                    <ReconRow key={r.bankLineId ?? r.paymentId ?? `row-${i}`} row={r} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <details className="legend" open>
            <summary>{t('recon.legend')}</summary>
            <dl>
              {RECONCILIATION_STATUSES.map((st) => (
                <div key={st} className="legend-item">
                  <dt>
                    <Chip tone={RECONCILIATION_TONE[st]}>{t(`recon.status.${st}`)}</Chip>
                  </dt>
                  <dd>{t(`recon.explain.${st}`)}</dd>
                </div>
              ))}
            </dl>
          </details>
        </section>
      ) : null}
    </>
  );
}

/** Rows that ask the merchant to do something carry their explanation inline. */
const ACTION_NEEDED = new Set<ReconciliationStatus>(['MISSING_IN_BANK', 'AMOUNT_MISMATCH']);

/** The bank's booking date: a day ('YYYY-MM-DD') or an instant (shown as its Skopje date). */
function formatBankDate(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDay(value) : formatDate(value);
}

function ReconRow({ row: r }: { row: ReconciliationRow }) {
  const { t, locale } = useI18n();
  const dash = '—';
  const who = r.payerName ?? r.counterpartyName ?? dash;
  return (
    <tr className={`recon-row recon-row--${r.status.toLowerCase()}`} data-status={r.status}>
      <td>
        <Chip tone={RECONCILIATION_TONE[r.status]} title={t(`recon.explain.${r.status}`)}>
          {t(`recon.status.${r.status}`)}
        </Chip>
        {ACTION_NEEDED.has(r.status) ? <div className="row-explain">{t(`recon.explain.${r.status}`)}</div> : null}
        {r.status === 'OTHER_INCOMING' && r.paymentId !== null ? (
          <div className="row-explain">{t('recon.explain.linkedOther')}</div>
        ) : null}
      </td>
      <td className="nowrap">{r.zinqSettledAt ? formatDateTime(r.zinqSettledAt, locale) : dash}</td>
      <td className="nowrap">{r.bankBookedAt ? formatBankDate(r.bankBookedAt) : dash}</td>
      <td>{who}</td>
      <td className="mono">{r.reference ?? dash}</td>
      <td className="num">{r.zinqAmountMinor !== null ? formatDen(r.zinqAmountMinor) : dash}</td>
      <td className={`num${r.status === 'AMOUNT_MISMATCH' ? ' strong warn-text' : ''}`}>
        {r.bankAmountMinor !== null ? formatDen(r.bankAmountMinor) : dash}
      </td>
      <td className="num">{r.netMinor !== null ? formatDen(r.netMinor) : dash}</td>
    </tr>
  );
}
